import type { ReactNode } from 'react';

export function Section({ title, help, children }: { title: string; help?: string; children: ReactNode }) {
  return (
    <section className="a-section">
      <header className="a-section-hd">
        <h2>{title}</h2>
        {help && <p>{help}</p>}
      </header>
      <div className="a-section-body">{children}</div>
    </section>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="a-field">
      <span className="a-label">{label}</span>
      {children}
      {hint && <span className="a-hint">{hint}</span>}
    </label>
  );
}

export function NumberField({
  label, value, min, max, step = 1, onChange, hint,
}: {
  label: string; value: number; min: number; max: number; step?: number;
  onChange: (v: number) => void; hint?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <span className="a-number">
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <input
          type="number" min={min} max={max} step={step} value={value}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
          }}
        />
      </span>
    </Field>
  );
}

export function Toggle({
  label, checked, onChange, hint,
}: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <label className="a-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="a-switch" aria-hidden="true" />
      <span className="a-toggle-text">
        <span className="a-label">{label}</span>
        {hint && <span className="a-hint">{hint}</span>}
      </span>
    </label>
  );
}

export function Segmented<T extends string>({
  label, value, options, onChange, hint,
}: {
  label: string; value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  hint?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <span className="a-seg" role="group">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={o.value === value}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </span>
    </Field>
  );
}

export function Select<T extends string | number>({
  label, value, options, onChange, hint,
}: {
  label: string; value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void; hint?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <select
        className="a-select"
        value={String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          const found = options.find((o) => String(o.value) === raw);
          if (found) onChange(found.value);
        }}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}
